import { ReactNode, useCallback, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { get, useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import { ReactComponent as InfoCircleIcon } from "assets/images/iconsV2/info-circle.svg";
import { classNames } from "helpers/commonHelpers";
import { getPreferredTimezoneMinDate } from "helpers/dateTimeHelper";
import usePermissions from "hooks/usePermissions";
import DatePicker from "components/v2/DatePicker/DatePicker";
import TimeInput from "components/v2/TimeInput/TimeInput";
import FieldErrorBadge from "@RequestFormV2/components/Badges/FieldErrorBadge";
import FieldWarningBadge from "@RequestFormV2/components/Badges/FieldWarningBadge";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import { useRelationalOnChangeValidation } from "@RequestFormV2/hooks/useRelationalOnChangeValidation";
import { getPastSelectableDateErrorPath, isDeadlineDateInPast } from "@RequestFormV2/validation/utils/isDeadlineDateInPast";
import {
  CONFIRMATION_AFTER_QUOTING_ERROR_PATH,
  validateDatetimeChain,
} from "@RequestFormV2/validation/validateDatetimeChain";

type ShipmentDateTimeFieldProps = {
  dateName: string;
  timeName: string;
  dateLabel: ReactNode;
  labelInfo?: string;
  required?: boolean;
  dateTestId?: string;
  timeTestId?: string;
  showConfirmationAfterQuotingError?: boolean;
  pickupExceedsWarningMessage?: string;
};

export default function ShipmentDateTimeField({
  dateName,
  timeName,
  dateLabel,
  labelInfo,
  required,
  dateTestId,
  timeTestId,
  showConfirmationAfterQuotingError,
  pickupExceedsWarningMessage,
}: ShipmentDateTimeFieldProps) {
  const intl = useIntl();
  const { parsedPermissions } = usePermissions();
  const preferredTimezone = parsedPermissions?.preferred_timezone;
  const minSelectableDate = useMemo(() => getPreferredTimezoneMinDate(preferredTimezone), [preferredTimezone]);
  const labelInfoTooltipId = `shipment-datetime-info-${dateName.replace(/[.[\]]+/g, "-")}`;
  const {
    formState: { errors },
    getValues,
    setError,
  } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const dateValue = useWatch({ name: dateName });
  const quotingDate = useWatch({ name: "shipment.quoting_deadline_date" });
  const quotingTime = useWatch({ name: "shipment.quoting_deadline_time" });
  const confirmationDate = useWatch({ name: "shipment.confirmation_date" });
  const confirmationTime = useWatch({ name: "shipment.confirmation_time" });

  const pastSelectableErrorPath = getPastSelectableDateErrorPath(dateName);
  const pastDateError = get(errors, pastSelectableErrorPath);

  useReactiveReviewClear(pastSelectableErrorPath, () => !isDeadlineDateInPast(dateValue), [dateValue]);

  useRelationalOnChangeValidation(
    (clear) => validateDatetimeChain(getValues, setError, intl, clear),
    [quotingDate, quotingTime, confirmationDate, confirmationTime],
    Boolean(showConfirmationAfterQuotingError),
  );

  const timeLabel = intl.formatMessage({ id: "general.time", defaultMessage: "Time" });
  const confirmationAfterQuotingError = showConfirmationAfterQuotingError
    ? get(errors, CONFIRMATION_AFTER_QUOTING_ERROR_PATH)
    : undefined;
  const showFieldGroup =
    Boolean(pickupExceedsWarningMessage) ||
    Boolean(pastDateError?.message) ||
    Boolean(confirmationAfterQuotingError?.message);

  const handleFieldChange = useCallback(
    (fieldName: string) => {
      if (saveContext) {
        saveContext.saveField(fieldName, getValues(fieldName)).catch(() => undefined);
      }
    },
    [getValues, saveContext],
  );

  return (
    <div
      className={classNames({
        "rfv2-shipment-datetime-field-group": showFieldGroup,
      })}
    >
      <div className="rfv2-schedule-datetime rfv2-shipment-datetime">
        <div className="rfv2-schedule-datetime__field">
          <label className={classNames("time-input-v2__label", "rfv2-shipment-datetime-label", { required })}>
            {dateLabel}
            {labelInfo && (
              <OverlayTrigger
                placement="top"
                delay={{ show: 250, hide: 0 }}
                overlay={<Tooltip id={labelInfoTooltipId}>{labelInfo}</Tooltip>}
              >
                <span className="rfv2-shipment-datetime-label__info" tabIndex={0} role="button" aria-label={labelInfo}>
                  <InfoCircleIcon width={14} height={14} />
                </span>
              </OverlayTrigger>
            )}
          </label>
          <DatePicker
            name={dateName}
            selectionMode="single"
            numberOfMonths={1}
            alignment="right"
            fullWidth
            required={required}
            minDate={minSelectableDate}
            hasError={Boolean(pastDateError?.message)}
            onDateSelect={() => handleFieldChange(dateName)}
            testId={dateTestId}
          />
        </div>
        <div className="rfv2-schedule-datetime__field">
          <TimeInput
            name={timeName}
            label={timeLabel}
            required={required}
            testId={timeTestId}
            onTimeSelect={() => handleFieldChange(timeName)}
          />
        </div>
      </div>
      {pastDateError?.message && <FieldErrorBadge message={String(pastDateError.message)} />}
      {pickupExceedsWarningMessage && <FieldWarningBadge message={pickupExceedsWarningMessage} />}
      {confirmationAfterQuotingError?.message && <FieldErrorBadge message={String(confirmationAfterQuotingError.message)} />}
    </div>
  );
}
